import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Shield, Loader2, X } from "lucide-react";

interface TOTPVerifyModalProps {
  isOpen: boolean;
  email: string;
  name: string;
  onVerify: (totpCode: string) => Promise<void>;
  onCancel: () => void;
  error?: string | null;
  onErrorClear?: () => void;
}

export const TOTPVerifyModal = ({ isOpen, email, name, onVerify, onCancel, error, onErrorClear }: TOTPVerifyModalProps) => {
  const [totpCode, setTotpCode] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (isOpen) {
      setTotpCode(["", "", "", "", "", ""]);
      setLocalError(null);
      setIsLoading(false);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (error) {
      setIsLoading(false);
      setTotpCode(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 0);
    }
  }, [error]);

  useEffect(() => {
    if (totpCode.some((d) => d !== "")) {
      setLocalError(null);
      if (error) {
        // Error is managed by parent, but we can clear local error
      }
    }
  }, [totpCode, error]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...totpCode];
    newCode[index] = value;
    setTotpCode(newCode);

    if (value) {
      if (localError) {
        setLocalError(null);
      }
      if (error && onErrorClear) {
        onErrorClear();
      }
    }

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !totpCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "Enter" && !isLoading) {
      e.preventDefault();
      const allFilled = totpCode.every(d => d !== "");
      if (allFilled) {
        handleVerify();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim().replace(/\D/g, "").slice(0, 6);
    if (pastedData.length > 0) {
      const newCode = [...totpCode];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newCode[i] = pastedData[i];
      }
      setTotpCode(newCode);
      
      if (localError) {
        setLocalError(null);
      }
      if (error && onErrorClear) {
        onErrorClear();
      }
      
      const nextEmptyIndex = newCode.findIndex((d) => d === "");
      if (nextEmptyIndex === -1) {
        inputRefs.current[5]?.focus();
      } else {
        inputRefs.current[nextEmptyIndex]?.focus();
      }
    }
  };

  const handleVerify = async (code?: string) => {
    const codeToVerify = code || totpCode.join("");
    if (codeToVerify.length !== 6) {
      setLocalError("Please enter all 6 digits");
      return;
    }

    setIsLoading(true);
    setLocalError(null);
    try {
      await onVerify(codeToVerify);
    } catch (err) {
      setIsLoading(false);
      setTotpCode(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 0);
    }
  };

  const displayError = error || localError;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-7 w-7 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl">Two-Factor Authentication</DialogTitle>
          <DialogDescription className="text-center space-y-1">
            <span className="block">Enter the 6-digit code from your authenticator app</span>
            <span className="block font-medium text-foreground">{name}</span>
            <span className="block text-xs text-muted-foreground">{email}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="flex justify-center gap-2">
            {totpCode.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={isLoading}
                className={`w-11 h-14 text-center text-2xl font-bold rounded-lg border-2 bg-background transition-all duration-200 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed ${displayError ? "border-destructive" : "border-border"}`}
              />
            ))}
          </div>

          {displayError && (
            <p className="text-center text-sm text-destructive animate-in fade-in-0">{displayError}</p>
          )}

          <p className="text-center text-xs text-muted-foreground">
            Open your authenticator app (Google Authenticator, Authy, etc.) to get the code
          </p>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onCancel} disabled={isLoading} className="flex-1">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={() => handleVerify()}
              disabled={isLoading || totpCode.some((d) => !d)}
              className="flex-1 gradient-primary hover:opacity-90"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
